import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type JSX, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { useToast } from "@/components/ui/toast-provider";
import type { ResponseAssetProjection } from "@/lib/chat-event-schemas";
import {
  type TurnShareAudience,
  type TurnShareGrantRead,
  type TurnSharePreviewResponse,
  type TurnShareScope,
  createTurnShare,
  listTurnShares,
  previewTurnShare,
  revokeTurnShare,
} from "@/lib/chat-turn-sharing";
import { copyTextToClipboard } from "@/lib/clipboard";
import { formatDate } from "@/lib/format";
// Reused, not reimplemented: the backend's turn-share preview emits the
// exact same `SafeVisualRelationProjection` wire shape report sharing does,
// so the same normalizer keeps a redacted relation STRUCTURALLY incapable
// of carrying alt text/caption/locator here too (see its doc comment in
// `@/lib/reports`).
import { toReportImageRelation } from "@/lib/reports";

interface ShareTurnDialogProps {
  sessionId: string;
  assistantMessageId: string;
  /** The owner's own live view of this turn's attached visuals
   * (`turn.responseAssets` in `routes/chat.tsx`, sourced from the
   * `response_assets` SSE event / `ChatMessageRead.response_assets`).
   * Used only to build the pre-share preview below — never sent anywhere,
   * and never rendered once an audience's anonymous projection would
   * redact the relation (see `TurnVisualPreview`). */
  assets: ResponseAssetProjection[];
  onClose: () => void;
}

const AUDIENCE_OPTIONS: { value: TurnShareAudience; label: string }[] = [
  { value: "beflow_team", label: "Equipo BeFlow (cualquier cuenta autenticada)" },
  { value: "anyone_with_link", label: "Cualquiera con el enlace (público)" },
];

const SCOPE_OPTIONS: { value: TurnShareScope; label: string }[] = [
  { value: "question_and_answer", label: "Pregunta y respuesta" },
  { value: "answer_only", label: "Solo la respuesta" },
];

function audienceLabel(audience: TurnShareAudience): string {
  return audience === "beflow_team" ? "Equipo" : "Enlace público";
}

function scopeLabel(scope: TurnShareScope): string {
  return scope === "question_and_answer" ? "Pregunta + respuesta" : "Solo respuesta";
}

interface CopyRecovery {
  readonly grantId: string;
  readonly url: string;
}

export function ShareTurnDialog({
  sessionId,
  assistantMessageId,
  assets,
  onClose,
}: ShareTurnDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [audience, setAudience] = useState<TurnShareAudience>("anyone_with_link");
  const [scope, setScope] = useState<TurnShareScope>("question_and_answer");
  const [error, setError] = useState<string | null>(null);
  const [copyRecovery, setCopyRecovery] = useState<CopyRecovery | null>(null);

  const queryKey = ["chat", "turn-shares", sessionId, assistantMessageId] as const;

  const sharesQuery = useQuery({
    queryKey,
    queryFn: () => listTurnShares({ sessionId, assistantMessageId }),
  });

  const createMutation = useMutation({
    mutationFn: () => createTurnShare({ sessionId, assistantMessageId, audience, scope }),
    onMutate: () => {
      setError(null);
    },
    onSuccess: async (result) => {
      const copied = await copyTextToClipboard(result.url);
      setCopyRecovery(copied ? null : { grantId: result.grant.grant_id, url: result.url });
      showToast({
        kind: copied ? "success" : "error",
        message: copied ? "Enlace creado y copiado." : "Enlace creado, pero no se pudo copiar.",
      });
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: () => setError("No se pudo crear el enlace. Intenta de nuevo."),
  });

  // The chat spec requires the owner to see the EXACT anonymous projection
  // before an `anyone_with_link` grant exists. A failed preview means they did
  // not see it, so creation is blocked rather than proceeding blind — the same
  // reason the projection itself is server-computed instead of guessed here.
  // Same query key as `TurnVisualPreview`, so React Query serves one request.
  const anonymousPreviewQuery = useQuery({
    queryKey: ["chat", "turn-share-preview", sessionId, assistantMessageId, audience, scope],
    queryFn: () => previewTurnShare({ sessionId, assistantMessageId, audience, scope }),
    enabled: audience === "anyone_with_link" && assets.length > 0,
  });
  const anonymousProjectionConfirmed =
    audience !== "anyone_with_link" || assets.length === 0 || anonymousPreviewQuery.isSuccess;

  const revokeMutation = useMutation({
    mutationFn: (grantId: string) => revokeTurnShare(grantId),
    onSuccess: (_data, grantId) => {
      setCopyRecovery((current) => (current?.grantId === grantId ? null : current));
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: () => setError("No se pudo revocar el enlace."),
  });

  async function retryCopy(): Promise<void> {
    if (!copyRecovery) return;
    const copied = await copyTextToClipboard(copyRecovery.url);
    showToast({
      kind: copied ? "success" : "error",
      message: copied ? "Enlace copiado." : "No se pudo copiar el enlace.",
    });
    if (copied) setCopyRecovery(null);
  }

  const activeShares = (sharesQuery.data ?? []).filter((share) => share.revoked_at === null);

  return (
    <Dialog open onClose={onClose} ariaLabel="Compartir respuesta" className="max-w-lg">
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-body-1 font-semibold text-foreground">Compartir respuesta</h2>

        <FieldGroup>
          <FieldSet>
            <FieldLegend>Con quién</FieldLegend>
            {AUDIENCE_OPTIONS.map((option) => (
              <Field key={option.value} orientation="horizontal" className="justify-start gap-2">
                <input
                  type="radio"
                  id={`share-audience-${option.value}`}
                  name="share-audience"
                  value={option.value}
                  checked={audience === option.value}
                  onChange={() => setAudience(option.value)}
                />
                <FieldLabel htmlFor={`share-audience-${option.value}`}>{option.label}</FieldLabel>
              </Field>
            ))}
          </FieldSet>

          <FieldSet>
            <FieldLegend>Qué mostrar</FieldLegend>
            {SCOPE_OPTIONS.map((option) => (
              <Field key={option.value} orientation="horizontal" className="justify-start gap-2">
                <input
                  type="radio"
                  id={`share-scope-${option.value}`}
                  name="share-scope"
                  value={option.value}
                  checked={scope === option.value}
                  onChange={() => setScope(option.value)}
                />
                <FieldLabel htmlFor={`share-scope-${option.value}`}>{option.label}</FieldLabel>
              </Field>
            ))}
          </FieldSet>
        </FieldGroup>

        <TurnVisualPreview
          sessionId={sessionId}
          assistantMessageId={assistantMessageId}
          hasAssets={assets.length > 0}
          audience={audience}
          scope={scope}
        />

        <div>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={
              createMutation.isPending || copyRecovery !== null || !anonymousProjectionConfirmed
            }
            size="sm"
          >
            {createMutation.isPending ? "Creando…" : "Crear enlace"}
          </Button>
          {!anonymousProjectionConfirmed && (
            <p className="mt-1 text-label-2 text-muted-foreground">
              No se puede crear un enlace público hasta poder mostrarte exactamente qué imágenes
              quedarían visibles.
            </p>
          )}
        </div>
        {error && <p className="text-label-2 text-destructive">{error}</p>}
        {copyRecovery ? (
          <fieldset
            aria-label="Recuperar enlace recién creado"
            className="space-y-2 rounded-md border border-destructive/50 bg-destructive/5 p-3"
          >
            <p className="text-label-2 text-destructive">
              El enlace se creó, pero no pudimos copiarlo. Cópialo antes de cerrar.
            </p>
            <input
              aria-label="Enlace recién creado"
              readOnly
              value={copyRecovery.url}
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-label-3 text-foreground"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => void retryCopy()}>
              Reintentar copia
            </Button>
          </fieldset>
        ) : null}

        <div className="flex flex-col gap-2">
          <h3 className="text-label-2 font-medium text-muted-foreground">Enlaces activos</h3>
          {sharesQuery.isLoading && <p className="text-label-2 text-muted-foreground">Cargando…</p>}
          {!sharesQuery.isLoading && activeShares.length === 0 && (
            <p className="text-label-2 text-muted-foreground">
              Todavía no compartiste esta respuesta.
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {activeShares.map((share) => (
              <ShareRow
                key={share.grant_id}
                share={share}
                onRevoke={() => revokeMutation.mutate(share.grant_id)}
                revoking={revokeMutation.isPending && revokeMutation.variables === share.grant_id}
              />
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Pre-share preview of the exact visual projection this exact grant would
 * produce for the selected audience — WITHOUT creating one
 * (`return-knowledge-images-across-surfaces` PR 4, closing the spec
 * requirement that the owner must see the exact projection — which
 * eligible assets stay included vs. redacted, with counts — before
 * activating a grant).
 *
 * Backed by `GET .../shares/preview` (`previewTurnShare`,
 * `src.chat.turn_sharing.TurnSharingService.preview`), which builds its
 * answer from the SAME projection functions
 * (`project_public_visual_relation` / `project_team_minimum_visual_
 * relation`, `src.knowledge_assets.visual_projection`) that
 * `resolve_public`/`resolve_team` use to serve the real link — so this
 * component never re-derives or assumes what "should" be redacted. If a
 * future change lifts the public-visual v1 barrier and some relation
 * starts including, this preview reflects that the moment the backend
 * does, with zero changes here.
 */
function TurnVisualPreview({
  sessionId,
  assistantMessageId,
  hasAssets,
  audience,
  scope,
}: {
  sessionId: string;
  assistantMessageId: string;
  hasAssets: boolean;
  audience: TurnShareAudience;
  scope: TurnShareScope;
}): JSX.Element | null {
  const previewQuery = useQuery({
    queryKey: ["chat", "turn-share-preview", sessionId, assistantMessageId, audience, scope],
    queryFn: () => previewTurnShare({ sessionId, assistantMessageId, audience, scope }),
    enabled: hasAssets,
  });

  if (!hasAssets) return null;

  return (
    <section
      aria-label="Vista previa de imágenes"
      className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-label-2"
    >
      {previewQuery.isLoading ? (
        <p className="text-muted-foreground">Cargando vista previa de imágenes…</p>
      ) : previewQuery.isError || !previewQuery.data ? (
        <p className="text-destructive">No se pudo cargar la vista previa de imágenes.</p>
      ) : (
        <TurnVisualPreviewBody preview={previewQuery.data} audience={audience} />
      )}
    </section>
  );
}

function TurnVisualPreviewBody({
  preview,
  audience,
}: {
  preview: TurnSharePreviewResponse;
  audience: TurnShareAudience;
}): JSX.Element {
  // The backend's `SafeVisualRelationProjection` wire shape never carries
  // `alt_text`/`caption`/`locator` on a redacted relation — normalizing it
  // into the strict two-state `ReportImageRelation` union here means a
  // redacted row below is STRUCTURALLY incapable of rendering those
  // fields, regardless of what a buggy/malicious backend response might
  // additionally include (mirrors `ProjectionMiniPreview` in
  // `@/components/reports/ShareDialog`).
  const imageRelations = preview.assets.map(toReportImageRelation);

  return (
    <>
      <p className="font-medium text-foreground">
        Imágenes: {preview.included_asset_count} incluidas · {preview.redacted_asset_count}{" "}
        redactada{preview.redacted_asset_count === 1 ? "" : "s"}
      </p>
      <p className="text-muted-foreground">
        {audience === "anyone_with_link"
          ? "Ninguna fuente de conocimiento califica hoy como pública: las imágenes de un enlace público siempre quedan redactadas, sin importar cuál elijas compartir."
          : "Vista conservadora: en el peor de los casos ningún destinatario ve estas imágenes. Los permisos vigentes de cada destinatario al abrir el enlace pueden revelar más o menos detalle que esta vista."}
      </p>
      <ul className="space-y-1">
        {imageRelations.map((relation) =>
          relation.state === "available" ? (
            <li key={relation.relation_id} className="text-foreground">
              {relation.alt_text}
            </li>
          ) : (
            <li key={relation.relation_id} className="text-muted-foreground">
              <em>Evidencia visual no compartida</em>
            </li>
          ),
        )}
      </ul>
    </>
  );
}

function ShareRow({
  share,
  onRevoke,
  revoking,
}: {
  share: TurnShareGrantRead;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-label-2">
      <div className="flex flex-col">
        <span className="font-medium text-foreground">
          {audienceLabel(share.audience)} · {scopeLabel(share.scope)}
        </span>
        <span className="text-muted-foreground">
          {share.token_prefix ? `${share.token_prefix}… · ` : ""}
          {formatDate(share.created_at)}
        </span>
      </div>
      <Button variant="outline" size="sm" onClick={onRevoke} disabled={revoking}>
        {revoking ? "Revocando…" : "Revocar"}
      </Button>
    </li>
  );
}
